import type { Subscription } from '../api/client';

/**
 * Which Plan rows Settings may show, derived from the subscription.
 *
 * The rule these encode is that **a term belongs to a tier, not to a row**.
 * Free has no term: both date columns are NULL on free, every path that sets
 * `tier='free'` clears them together, and nothing reads them for a free
 * account. Settings used to gate its "Active Until" row on
 * `subscription_ends_at` alone, which held right up until a row carried a term
 * its tier no longer justified — `005_drop_iap_and_trial` folded the retired
 * `basic` tier into free with a bare `UPDATE subscriptions SET tier = 'free'`
 * and left the old plan's dates behind. Those accounts read "FREE, Active Until
 * <date>": a free plan that expires, which the app has no meaning for.
 *
 * `006_clear_free_tier_terms` repaired the rows. These functions are why the
 * same drift cannot reach the screen if it is ever reintroduced — deriving at
 * render time rather than trusting the stored columns, for the same reason
 * `deriveActivityStatus` does (see displayStatus.ts).
 */
type PlanFields = Pick<
  Subscription,
  'tier' | 'subscription_ends_at' | 'subscription_source'
>;

/**
 * The term to render, or null when there is none to show. Free yields null
 * whatever the column holds.
 */
export function planTermFor(
  subscription: Pick<PlanFields, 'tier' | 'subscription_ends_at'> | null | undefined,
): string | null {
  if (!subscription) return null;
  if (subscription.tier === 'free') return null;
  return subscription.subscription_ends_at || null;
}

/**
 * The grant source to render, or null. Gated the same way: "Granted via Access
 * code" on a free row describes a plan the account no longer has.
 */
export function planSourceFor(
  subscription: Pick<PlanFields, 'tier' | 'subscription_source'> | null | undefined,
): string | null {
  if (!subscription) return null;
  if (subscription.tier === 'free') return null;
  return subscription.subscription_source || null;
}

/**
 * Whether to offer the Enterprise enquiry row.
 *
 * True while the subscription is still loading, deliberately: the tier badge
 * already defaults to FREE over the same absent value, so the card does not
 * rearrange itself when the fetch lands.
 */
export function offersEnterpriseEnquiry(
  subscription: Pick<PlanFields, 'tier'> | null | undefined,
): boolean {
  return subscription?.tier !== 'enterprise';
}
